import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { alpha, ListItemText, Menu, MenuItem, Paper, Stack } from '@mui/material'
import { selectActiveChartId, selectChartConfig, selectChartDrawings } from '../../../selectors/chart'
import { AppContext } from '../../../App'
import { DrawingTypes } from '../drawingTools/drawing.interface'
import { ChartEvents } from '../chart.events'
import DrawingToolMenu from './drawingToolMenu/drawingToolMenu'
import { lineToolMenuItems } from './drawingToolMenu/lineToolMenuItems'
import { shapesToolMenuItems } from './drawingToolMenu/shapesToolMenuItems'
import { fibonacciToolMenuItems } from './drawingToolMenu/fibonacciTools'
import { forecastToolMenuItems } from './drawingToolMenu/forecastToolsMenuItems'
import { patternsToolMenuItems } from './drawingToolMenu/patternToolsMenu'
import { textToolsMenuItems } from './drawingToolMenu/textToolsMenu'
import MagnetIcon from '../../../assets/icons/drawingTools/magnet'
import ZoomInIcon from '../../../assets/icons/drawingTools/zoomInTool'
import { ExtraSmallButton, NonInteractiveTooltip } from '../../custom'
import { SeriesGroupCategory } from '../../../pages/home'
import { selectMultiPaneFullScreen } from '../../../selectors/common'
import { MULTIPANE_FULLSCREEN_MODE_ID } from '../../../assets/constants'
import DeleteIcon from '../../../assets/icons/drawingTools/deleteIcon'
import { oiProfileMenuItems } from './drawingToolMenu/oiProfileMenuItems'
import OiDrawingToolMenu from './drawingToolMenu/oiDrawingToolMenu'
import { ActionType, useUndoRedoContext } from "../../UndoRedoFunctonalities/UndoRedo.context";
import ThinArrowUpIcon from '../../../assets/icons/ThinArrowUpIcon'


type ChartDrawingToolbarProps = {
    disable?: boolean,
    onDrawingClick: (drawingType: DrawingTypes, options: Record<string, any>, enter?: boolean) => void
}
const ChartDrawingToolbar = ({ disable = false, onDrawingClick }: ChartDrawingToolbarProps) => {
    const activeChartId = useSelector(selectActiveChartId)
    const appContext = useContext(AppContext)
    const drawings = useSelector(selectChartDrawings(activeChartId))
    const chartConfig = useSelector(selectChartConfig(activeChartId))
    const [activeDrawing, setActiveDrawing] = useState<DrawingTypes | null>(null)
    const { push } = useUndoRedoContext()
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
    const buttonRef = useRef<HTMLButtonElement | null>(null)
    const [magnetMode, setMagnetMode] = useState(Boolean(window.localStorage.getItem('chart_magnet_mode') || ''))
    const [zoomMode, setZoomMode] = useState(false)
    const count = useMemo(() => {
        if (!chartConfig?.seriesGroups) return 0
        return Object.values(chartConfig.seriesGroups).filter(
            (sg) => sg.category !== SeriesGroupCategory.SERIES_SECURITY
        ).length
    }, [chartConfig?.seriesGroups])

    const onDrawingActive = useCallback((event: CustomEvent) => {
        setActiveDrawing(event.detail as DrawingTypes)
    }, [])

    const onDrawingInactive = useCallback(() => {
        setActiveDrawing(null)
    }, [])

    const onRemoveDrawingsClicked = useCallback(() => {
        drawings?.forEach((drawing: {
            id: string
        }) => {
            const settings = appContext.getChart(activeChartId).drawingManager.getSettingsValues(drawing.id)
            if (settings && !settings['indicator']) {
                appContext.getChart(activeChartId).drawingManager.removeDrawing(drawing.id, true, false)
            }
        })
        const drawingItem = {
            chartId: activeChartId,
            drawing: drawings
        }
        push(drawingItem, "Remove Drawing From Toolbar", ActionType.REMOVE_DRAWING)
    }, [drawings, appContext, activeChartId])

    const onRemoveIndicators = useCallback(() => {
        appContext.getChart(activeChartId).removeAllIndicators()
    }, [activeChartId, appContext])

    useEffect(() => {
        const chart = appContext.getChart(activeChartId)
        if (chart) {
            chart.subscribeEvent(ChartEvents.DRAWING_ACTIVE, onDrawingActive)
            chart.subscribeEvent(ChartEvents.DRAWING_INACTIVE, onDrawingInactive)
        }
        return () => {
            if (chart) {
                chart.unsubscribeEvent(ChartEvents.DRAWING_ACTIVE, onDrawingActive)
                chart.unsubscribeEvent(ChartEvents.DRAWING_INACTIVE, onDrawingInactive)
            }
        }
    }, [appContext, activeChartId, onDrawingActive, onDrawingInactive])

    const onClick = useCallback((drawingType: DrawingTypes, options: Record<string, any>) => {
        if (drawingType === 'zoomIn') {
            setZoomMode(true)
        } else {
            setZoomMode(false)
        }
        if (activeDrawing !== drawingType) {
            onDrawingClick(drawingType, options, true)
        } else {
            onDrawingClick(drawingType, options, false)
        }
    }, [activeDrawing, onDrawingClick])

    const onMagnetButtonClick = useCallback(() => {
        setMagnetMode(!magnetMode)
        appContext.getChart(activeChartId).drawingManager.setMagnetMode(!magnetMode)
    }, [activeChartId, appContext, magnetMode])

    const handleClick = useCallback(() => {
        setAnchorEl(buttonRef.current)
    }, [])

    const handleClose = useCallback(() => {
        setAnchorEl(null)
    }, [])

    useEffect(() => {
        if (activeDrawing === null) {
            setZoomMode(false)
        }
    }, [activeDrawing])

    const container = useSelector(selectMultiPaneFullScreen)

    return (
        <Paper
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 1,
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: '4px',
                zIndex: 10,
                height: 'auto'
            }}
        >
            <OiDrawingToolMenu disable={disable} items={oiProfileMenuItems} />
            <DrawingToolMenu disable={disable} activeToolId={activeDrawing} items={lineToolMenuItems}
                onClick={onClick} />
            <DrawingToolMenu disable={disable} activeToolId={activeDrawing} items={shapesToolMenuItems}
                onClick={onClick} />
            <DrawingToolMenu disable={disable} activeToolId={activeDrawing} items={fibonacciToolMenuItems}
                onClick={onClick} />
            <DrawingToolMenu disable={disable} activeToolId={activeDrawing} items={forecastToolMenuItems}
                onClick={onClick} />
            <DrawingToolMenu disable={disable} activeToolId={activeDrawing} items={patternsToolMenuItems}
                onClick={onClick} />
            <DrawingToolMenu disable={disable} activeToolId={activeDrawing} items={textToolsMenuItems} onClick={onClick}
            />
            <NonInteractiveTooltip title={magnetMode ? 'Disable Magnet Mode' : 'Enable Magnet Mode'} placement="top"
                arrow={false}>
                <span>
                    <ExtraSmallButton
                        color={magnetMode ? 'primary' : 'inherit'}
                        sx={{ p: 0, mt: '4px', mx: '2px' }}
                        disabled={disable} onClick={onMagnetButtonClick}>
                        <MagnetIcon />
                    </ExtraSmallButton>
                </span>
            </NonInteractiveTooltip>
            <NonInteractiveTooltip title="Zoom In" placement="top" arrow={false}>
                <span>
                    <ExtraSmallButton
                        color={zoomMode ? 'primary' : 'inherit'} disabled={disable}
                        sx={{ p: 0, mt: '4px', mx: '2px' }}
                        onClick={() => onClick('zoomIn', {})}>
                        <ZoomInIcon />
                    </ExtraSmallButton>
                </span>
            </NonInteractiveTooltip>
            <Stack alignItems={'center'}
                sx={{
                    '&:hover .drawing-options-menu': {
                        visibility: 'visible !important'
                    },
                    position: 'relative'
                }}>
                <NonInteractiveTooltip title="Remove Drawings" placement="top" arrow={false}>
                    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
                        <ExtraSmallButton
                            onClick={handleClick}
                            color="inherit"
                            ref={buttonRef}
                            disabled={disable}
                            sx={{
                                visibility: Boolean(anchorEl) ? 'visible' : 'hidden',
                                bgcolor: theme => Boolean(anchorEl) ? alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity) : 'transparent',
                                position: 'absolute',
                                top: '-3px',
                                height: '10px',
                                p: 0,
                                margin: 0,
                                width: '32px',
                                zIndex: 1,
                                borderTopLeftRadius: 0,
                                borderTopRightRadius: 0
                            }}
                            className="drawing-options-menu"
                        >
                            <ThinArrowUpIcon sx={{ fontSize: '12px' }} />
                        </ExtraSmallButton>
                        <ExtraSmallButton
                            color={'inherit'}
                            disabled={disable}
                            onClick={handleClick}
                            sx={{ p: 0, mt: '4px', mx: '2px' }}
                        >
                            <DeleteIcon />
                        </ExtraSmallButton>
                    </span>
                </NonInteractiveTooltip>
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                    elevation={3}
                    container={container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'center'
                    }}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center'
                    }}
                >
                    <MenuItem
                        onClick={() => {
                            onRemoveDrawingsClicked()
                            handleClose()
                        }}>
                        <ListItemText>{`Remove ${drawings?.length ?? 0} drawings`}</ListItemText>
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            onRemoveIndicators()
                            handleClose()
                        }}>
                        <ListItemText>{`Remove ${count} indicators`}</ListItemText>
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            onRemoveDrawingsClicked()
                            onRemoveIndicators()
                            handleClose()
                        }}>
                        <ListItemText>{`Remove ${drawings?.length ?? 0} drawings and ${count} indicators`}</ListItemText>
                    </MenuItem>
                </Menu>
            </Stack>
        </Paper>
    )
}

export default memo(ChartDrawingToolbar)
